import type { MeasurementsStatsModes } from '@vy/api-definition';
import {
  GetMeasurementsStatsQueryResponseSchema,
  MEASUREMENTS_STATS_MODES,
  type GetMeasurementsStatsQueryResponse,
} from '@vy/api-definition';
import type { Optional } from '@vy/utils';
import { isNotNullOrUndefined, validateNotNullOrUndefined, vySerialize } from '@vy/utils';
import { useQuery } from 'react-query';
import { getPrivateApi } from '../../../services/api';
import { queryKeys } from '../../../services/query-key/query-keys';
import type { CustomUseQueryOptions } from '../requests-types/custom-use-query-options';
import type { GetMeasurementsStatsRequest } from './use-measurements-stats-types/get-measurements-stats-request';

export type UseMeasurementsStatsInput = {
  startTime: Optional<Date>;
  endTime: Optional<Date>;
  customersIds: Optional<string[]>;
  eventNames: Optional<string[]>;
  metersIds: Optional<string[]>;
  mode: Optional<MeasurementsStatsModes>;
  page: Optional<number>;
  pageSize: Optional<number>;
};

export function useMeasurementsStats(
  useMeasurementsStatsInput: UseMeasurementsStatsInput,
  options?: CustomUseQueryOptions<GetMeasurementsStatsQueryResponse>,
) {
  const { startTime, endTime, customersIds, eventNames, metersIds, page, pageSize } =
    useMeasurementsStatsInput;
  const api = getPrivateApi();

  const mode = useMeasurementsStatsInput.mode ?? MEASUREMENTS_STATS_MODES.QUERY;

  const isParamsValid =
    isNotNullOrUndefined(startTime) &&
    isNotNullOrUndefined(endTime) &&
    (isNotNullOrUndefined(customersIds) || isNotNullOrUndefined(eventNames));

  const customerParts = customersIds ?? [];
  const eventParts = eventNames ?? [];
  const meterParts = metersIds ?? [];
  const queryKey = queryKeys.measurementsStats(
    startTime?.toString() ?? '',
    endTime?.toString() ?? '',
    ...customerParts,
    ...eventParts,
    ...meterParts,
    page?.toString() ?? '',
    pageSize?.toString() ?? '',
  );

  return useQuery(
    queryKey,
    async () => {
      validateNotNullOrUndefined(startTime);
      validateNotNullOrUndefined(endTime);

      const getMeasurementsStatsRequest = buildGetMeasurementsStatsRequest(
        startTime,
        endTime,
        customersIds,
        eventNames,
        metersIds,
        mode,
        page,
        pageSize,
      );

      const getMeasurementsStatsResponse = await api.getMeasurementsStats(getMeasurementsStatsRequest);

      return GetMeasurementsStatsQueryResponseSchema.parse(getMeasurementsStatsResponse);
    },
    {
      enabled: isParamsValid,
      ...options,
    },
  );
}

export function buildGetMeasurementsStatsRequest(
  startTime: Date,
  endTime: Date,
  customersIds: Optional<string[]>,
  eventNames: Optional<string[]>,
  metersIds: Optional<string[]>,
  mode: Optional<MeasurementsStatsModes>,
  page: Optional<number>,
  pageSize: Optional<number>,
): GetMeasurementsStatsRequest {
  return {
    startTime: startTime,
    endTime: endTime,
    customersIds: isNotNullOrUndefined(customersIds) ? vySerialize(customersIds) : undefined,
    eventNames: isNotNullOrUndefined(eventNames) ? vySerialize(eventNames) : undefined,
    metersIds: isNotNullOrUndefined(metersIds) ? vySerialize(metersIds) : undefined,
    mode: isNotNullOrUndefined(mode) ? mode : MEASUREMENTS_STATS_MODES.QUERY,
    page: isNotNullOrUndefined(page) ? page : undefined,
    pageSize: isNotNullOrUndefined(pageSize) ? pageSize : undefined,
  };
}
